/**
 * Resolve highlight spans against exemplar paragraphs.
 *
 * Spans are authored as verbatim substrings and validated at seed time, so the
 * exact match is the fast path. Authoring drift (a curly apostrophe swapped for a
 * straight one, a line-wrapped quote picking up a newline, an em dash typed as a
 * hyphen) would otherwise silently drop a mark, so a second pass matches against
 * a normalized copy of the paragraph and maps the hit back to original offsets.
 *
 * A span that still cannot be placed is returned in `unmatched` — never dropped.
 * The comparison view lists those under the vignette as "Also worth noticing."
 *
 * These spans are the ANSWER KEY. Placing them says where they sit; only the
 * comparison view, after the learner has marked the text themselves, may render
 * them.
 */

import type { DojoHighlightSpan } from '@/types/dojo';

/** Where one authored span landed, in original-paragraph offsets. */
export interface HighlightPlacement {
  paragraph: number;
  start: number;
  end: number;
  span: DojoHighlightSpan;
}

export interface PlaceHighlightsResult {
  placements: HighlightPlacement[];
  unmatched: DojoHighlightSpan[];
}

interface NormalizedText {
  text: string;
  /** Original start offset of normalized character i. */
  starts: number[];
  /** Original end offset (exclusive) of normalized character i. */
  ends: number[];
}

const QUOTE_REPLACEMENTS: Record<string, string> = {
  '‘': "'",
  '’': "'",
  '‚': "'",
  '‛': "'",
  '′': "'",
  '´': "'",
  '`': "'",
  '“': '"',
  '”': '"',
  '„': '"',
  '‟': '"',
  '″': '"',
  '–': '-',
  '—': '-',
  '‒': '-',
  '―': '-',
  '‐': '-',
  '‑': '-',
  '−': '-',
};

function isWhitespace(char: string): boolean {
  return /\s/.test(char);
}

/**
 * Collapse whitespace runs to a single space and fold quote/dash variants,
 * recording where each normalized character came from.
 */
function normalize(source: string): NormalizedText {
  const chars: string[] = [];
  const starts: number[] = [];
  const ends: number[] = [];

  let i = 0;
  while (i < source.length) {
    const char = source[i];
    if (isWhitespace(char)) {
      const runStart = i;
      while (i < source.length && isWhitespace(source[i])) i += 1;
      chars.push(' ');
      starts.push(runStart);
      ends.push(i);
      continue;
    }
    chars.push(QUOTE_REPLACEMENTS[char] ?? char);
    starts.push(i);
    ends.push(i + 1);
    i += 1;
  }

  return { text: chars.join(''), starts, ends };
}

/** Drop whitespace off both ends of a range without moving into the text. */
export function trimRange(source: string, start: number, end: number): [number, number] {
  let nextStart = start;
  let nextEnd = end;
  while (nextStart < nextEnd && isWhitespace(source[nextStart])) nextStart += 1;
  while (nextEnd > nextStart && isWhitespace(source[nextEnd - 1])) nextEnd -= 1;
  return [nextStart, nextEnd];
}

/**
 * First occurrence of `quote` in `paragraph`: exact substring, else normalized
 * match mapped back onto the original string. Returns null when neither hits.
 */
export function locateQuote(
  paragraph: string,
  quote: string,
  normalizedParagraph?: NormalizedText
): { start: number; end: number } | null {
  const trimmedQuote = quote.trim();
  if (!trimmedQuote) return null;

  const exact = paragraph.indexOf(trimmedQuote);
  if (exact !== -1) {
    return { start: exact, end: exact + trimmedQuote.length };
  }

  const normalizedSource = normalizedParagraph ?? normalize(paragraph);
  const normalizedQuote = normalize(trimmedQuote).text.trim();
  if (!normalizedQuote) return null;

  const hit = normalizedSource.text.indexOf(normalizedQuote);
  if (hit === -1) return null;

  const originalStart = normalizedSource.starts[hit];
  const originalEnd = normalizedSource.ends[hit + normalizedQuote.length - 1];
  const [start, end] = trimRange(paragraph, originalStart, originalEnd);
  return end > start ? { start, end } : null;
}

/**
 * Resolve every span to offsets in its paragraph.
 *
 * Spans are honored in the order given; a span whose first occurrence overlaps an
 * already-placed span is reported as unmatched rather than silently truncating
 * either mark. Callers that render the key beside the learner's own marks need
 * the offsets, not pre-split segments, so this is the primitive and
 * `applyHighlights` is the convenience built on it.
 */
export function placeHighlights(
  paragraphs: string[],
  spans: DojoHighlightSpan[]
): PlaceHighlightsResult {
  const unmatched: DojoHighlightSpan[] = [];
  const placements: HighlightPlacement[] = [];
  const placementsByParagraph = new Map<number, HighlightPlacement[]>();
  const normalizedCache = new Map<number, NormalizedText>();

  for (const span of spans ?? []) {
    const paragraphIndex = span?.paragraph;
    const paragraph =
      typeof paragraphIndex === 'number' ? paragraphs[paragraphIndex] : undefined;

    if (typeof paragraph !== 'string') {
      unmatched.push(span);
      continue;
    }

    let normalized = normalizedCache.get(paragraphIndex);
    if (!normalized) {
      normalized = normalize(paragraph);
      normalizedCache.set(paragraphIndex, normalized);
    }

    const located = locateQuote(paragraph, span.quote ?? '', normalized);
    if (!located) {
      unmatched.push(span);
      continue;
    }

    const placed = placementsByParagraph.get(paragraphIndex) ?? [];
    const overlaps = placed.some(
      (existing) => located.start < existing.end && existing.start < located.end
    );
    if (overlaps) {
      unmatched.push(span);
      continue;
    }

    const placement: HighlightPlacement = { ...located, paragraph: paragraphIndex, span };
    placed.push(placement);
    placementsByParagraph.set(paragraphIndex, placed);
    placements.push(placement);
  }

  return { placements, unmatched };
}
